import { DebugLogger } from './logger';

/**
 * Converts base64url to base64
 * JWTs use base64url encoding which replaces + with -, / with _, and removes padding
 * @param base64url - The base64url string to convert
 * @returns Standard base64 string
 */
const base64urlToBase64 = (base64url: string): string => {
  // Replace base64url characters with base64 characters
  let base64 = base64url.replaceAll('-', '+').replaceAll('_', '/');

  // Add padding if needed
  const padding = base64.length % 4;
  if (padding > 0) {
    base64 += '='.repeat(4 - padding);
  }

  return base64;
};

/**
 * Decodes the payload of a JWT token without validation
 * @param token - The JWT token to decode
 * @returns The decoded payload object or null if decoding fails
 */
export const decodeJwtPayload = (token: string): { sub?: string } | null => {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');

    if (parts.length !== 3) {
      DebugLogger.warn(
        'Invalid JWT format: expected 3 parts separated by dots',
        {
          partsCount: parts.length,
        }
      );
      return null;
    }

    // Get the payload (middle part)
    const payload = parts[1];

    // Convert base64url to base64 (JWTs use base64url encoding)
    const base64Payload = base64urlToBase64(payload);

    // Base64 decode the payload
    // Note: Using atob (browser API) for base64 decoding
    const decodedPayload = atob(base64Payload);

    // Parse JSON
    return JSON.parse(decodedPayload);
  } catch (error) {
    // If decoding fails, log and return null
    DebugLogger.error('Failed to decode JWT payload:', error);
    return null;
  }
};
